// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * glossary.ts — jargon → plain-English definitions plus a tiny click-to-define
 * tooltip. Any element with `data-term` becomes a definable term.
 */

export const GLOSSARY: Record<string, string> = {
  webp: 'A modern web image format with excellent compression — typically 25–35% smaller than JPEG at the same quality. Supported by every current browser.',
  jpeg: 'The universal photo format. Slightly larger than WebP or AVIF, but every website, printer and app on earth opens it.',
  avif: 'A newer format based on the AV1 video codec. It makes the smallest files of all — great for the web — but encoding takes longer and a few older apps can\'t open it yet.',
  png: 'A lossless format — every pixel is preserved exactly. Best for screenshots, logos and sharp graphics. Pixpress optimises PNGs rather than throwing detail away.',
  mozjpeg: 'Mozilla\'s JPEG encoder — it squeezes noticeably smaller files than the one built into your operating system, at the same visual quality.',
  oxipng: 'A lossless PNG optimiser. It rewrites the file to be as small as possible without changing a single pixel.',
  quality: 'How much detail the lossy encoder keeps. Higher looks better but makes a bigger file; lower saves space. It has no effect on PNG, which is always lossless.',
  lossy: 'Compression that discards detail your eye is least likely to notice to make the file smaller. JPEG, WebP and AVIF are lossy; PNG is not.',
  lossless: 'Compression that shrinks the file while keeping every pixel identical to the original. PNG is lossless.',
  wasm: 'WebAssembly — a way to run fast, compiled code (here, the same encoders desktop tools use) directly and securely inside your browser tab.',
  'web-worker': 'A background thread in your browser. The heavy encoding runs here so the page never freezes while it works.',
  metadata: 'Hidden data tucked inside a photo — GPS location, camera model, date and time. Re-encoding writes fresh pixels, so all of it is dropped.',
  resize: 'Optionally shrink the image\'s dimensions so its longest edge fits within a pixel limit. Huge camera photos get much smaller with no visible loss on screen.',
};

let tooltip: HTMLElement | null = null;

export function initGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const link = target.closest<HTMLElement>('.glossary-link');
    if (link) {
      ev.preventDefault();
      const key = link.dataset.term ?? '';
      showTooltip(link, GLOSSARY[key] ?? 'No definition available.');
    } else if (!target.closest('.glossary-tip')) {
      hideTooltip();
    }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') hideTooltip();
  });
}

function showTooltip(anchor: HTMLElement, text: string): void {
  hideTooltip();
  tooltip = document.createElement('div');
  tooltip.className = 'glossary-tip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.textContent = text;
  document.body.appendChild(tooltip);

  const r = anchor.getBoundingClientRect();
  const tipR = tooltip.getBoundingClientRect();
  let left = r.left + window.scrollX;
  left = Math.min(left, window.scrollX + window.innerWidth - tipR.width - 12);
  left = Math.max(left, window.scrollX + 12);
  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${r.bottom + window.scrollY + 8}px`;
}

function hideTooltip(): void {
  tooltip?.remove();
  tooltip = null;
}

/** Wrap a term in a glossary link span. */
export function term(label: string, key: string): string {
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${label}</span>`;
}
